/**
 * X ADS › CREATIVES — /x-ads/creatives
 *
 * The promotable user's posts (published / draft / scheduled; promoted-only, organic or all),
 * a "new post" form, and the ads account's image library with upload.
 */
import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { FileText, Image as ImageIcon, ImagePlus, Plus, RefreshCw } from 'lucide-react';
import toast from 'react-hot-toast';
import { Button } from '@/components/Button';
import { Card } from '@/components/Card';
import { EmptyState } from '@/components/EmptyState';
import { Modal } from '@/components/Modal';
import { PostComposer } from '@/components/x-ads/PostComposer';
import { RetryLine, XAdsShell } from '@/components/x-ads/XAdsShell';
import { XSelect } from '@/components/x-ads/XSelect';
import { useXConnection } from '@/lib/x-ads/connection';
import { dateTime } from '@/lib/x-ads/format';
import { errMsg, xAdsService, type XMedia, type XPost } from '@/services/x-ads.service';

const TYPES = [['PUBLISHED', 'Published'], ['DRAFT', 'Drafts'], ['SCHEDULED', 'Scheduled']];
const TIMELINES = [['NULLCAST', 'Promoted-only'], ['ORGANIC', 'Organic'], ['ALL', 'All']];

export default function XAdsCreatives() {
  const { uid } = useXConnection();
  const [params, setParams] = useSearchParams();
  const tab = params.get('tab') === 'media' ? 'media' : 'posts';
  const [composing, setComposing] = useState(false);
  const [revision, setRevision] = useState(0);

  return <XAdsShell section="Creatives" description="Posts your campaigns promote, and the images they use."
    actions={<Button onClick={() => setComposing(true)} iconLeft={<Plus size={15} />}>New post</Button>}>
    <div className="xa-subtabs" role="group" aria-label="Creative views">
      <button type="button" aria-pressed={tab === 'posts'} onClick={() => setParams({ tab: 'posts' })}><FileText size={14} />Posts</button>
      <button type="button" aria-pressed={tab === 'media'} onClick={() => setParams({ tab: 'media' })}><ImageIcon size={14} />Media library</button>
    </div>
    {tab === 'posts' ? <Posts uid={uid} onNew={() => setComposing(true)} key={revision} /> : <Media uid={uid} />}
    <Modal open={composing} onClose={() => setComposing(false)} title="New promoted-only post" widthClass="max-w-4xl">
      <PostComposer uid={uid} onCreated={() => { setComposing(false); setRevision((r) => r + 1); }} />
    </Modal>
  </XAdsShell>;
}

function Posts({ uid, onNew }: { uid: string; onNew: () => void }) {
  const [type, setType] = useState('PUBLISHED');
  const [timeline, setTimeline] = useState('NULLCAST');
  const [posts, setPosts] = useState<XPost[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    if (!uid) return;
    setLoading(true); setError(null);
    try { setPosts(await xAdsService.posts(uid, type, timeline)); }
    catch (e) { setError(errMsg(e, 'Unable to load posts.')); }
    finally { setLoading(false); }
  }, [uid, type, timeline]);
  useEffect(() => { setPosts(null); load(); }, [load]);

  return <>
    <div className="mb-3 flex flex-wrap items-center gap-2">
      <div className="w-40"><XSelect value={type} onChange={(v) => v && setType(v)} options={TYPES.map(([v, l]) => ({ value: v, label: l }))} /></div>
      <div className="w-44"><XSelect value={timeline} onChange={(v) => v && setTimeline(v)} options={TIMELINES.map(([v, l]) => ({ value: v, label: l }))} /></div>
      <Button variant="ghost" size="sm" loading={loading} onClick={load} iconLeft={<RefreshCw size={14} />}>Refresh</Button>
    </div>
    {error && <RetryLine message={error} onRetry={load} />}
    {!posts && !error && <div className="skeleton h-48 rounded-2xl" aria-label="Loading posts" />}
    {posts?.length === 0 && <EmptyState icon={<FileText size={28} />} title="No posts here"
      description="Promoted-only posts never appear on your profile; they run only as ads. Create one to use it in a campaign."
      action={<Button onClick={onNew} iconLeft={<Plus size={15} />}>New post</Button>} />}
    {!!posts?.length && <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
      {posts.map((p, i) => <Card key={p.id ?? i} padded={false} className="overflow-hidden">
        {p.mediaUrls?.[0] && <img src={p.mediaUrls[0]} alt="" className="h-40 w-full object-cover" loading="lazy" />}
        <div className="p-4">
          <p className="line-clamp-4 whitespace-pre-wrap break-words text-sm">{p.text || <span className="text-text-muted">No text</span>}</p>
          <div className="mt-3 flex flex-wrap items-center gap-1.5">
            {p.name && <span className="xa-chip">{p.name}</span>}
            {p.cardUri && <span className="xa-chip !bg-info-soft !text-info">Card</span>}
            {p.nullcast && <span className="xa-chip">Promoted-only</span>}
          </div>
          <p className="xa-muted mt-2">{dateTime(p.createdAt)} · ID {p.id}</p>
        </div>
      </Card>)}
    </div>}
  </>;
}

function Media({ uid }: { uid: string }) {
  const [media, setMedia] = useState<XMedia[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);

  const load = useCallback(async () => {
    if (!uid) return;
    setError(null);
    try { setMedia(await xAdsService.media(uid)); }
    catch (e) { setError(errMsg(e, 'Unable to load the media library.')); }
  }, [uid]);
  useEffect(() => { load(); }, [load]);

  const upload = async (file: File | undefined) => {
    if (!file) return;
    if (!['image/jpeg', 'image/png', 'image/gif', 'image/webp'].includes(file.type) || file.size > 5 * 1024 * 1024) {
      toast.error('Images must be JPEG, PNG, GIF or WEBP, 5 MB or smaller.');
      return;
    }
    setUploading(true);
    try { await xAdsService.uploadImage(uid, file); toast.success('Image added to the media library'); await load(); }
    catch { /* toasted */ }
    finally { setUploading(false); }
  };

  return <>
    <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
      <p className="xa-muted">Images in the ads account's library can be reused in posts and cards.</p>
      <label className={`inline-flex cursor-pointer items-center gap-2 rounded-xl border border-border px-3 py-2 text-sm hover:bg-surface-alt ${uploading ? 'pointer-events-none opacity-60' : ''}`}>
        {uploading ? <RefreshCw size={15} className="animate-spin" /> : <ImagePlus size={15} />}{uploading ? 'Uploading…' : 'Upload image'}
        <input type="file" accept="image/jpeg,image/png,image/gif,image/webp" className="sr-only" disabled={uploading} onChange={(e) => { upload(e.target.files?.[0]); e.target.value = ''; }} />
      </label>
    </div>
    {error && <RetryLine message={error} onRetry={load} />}
    {!media && !error && <div className="skeleton h-48 rounded-2xl" aria-label="Loading media" />}
    {media?.length === 0 && <EmptyState icon={<ImageIcon size={28} />} title="The media library is empty" description="Upload an image to use it in posts and cards." />}
    {!!media?.length && <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
      {media.map((m, i) => <figure key={m.mediaKey ?? i} className="overflow-hidden rounded-xl border border-border bg-surface">
        {m.mediaUrl ? <img src={m.mediaUrl} alt={m.name || ''} className="aspect-square w-full object-cover" loading="lazy" /> : <div className="flex aspect-square items-center justify-center bg-surface-alt"><ImageIcon size={22} className="text-text-muted" /></div>}
        <figcaption className="p-2"><p className="truncate text-xs font-medium">{m.name || m.fileName || m.mediaKey}</p><p className="truncate text-[11px] text-text-muted">{dateTime(m.createdAt)}</p></figcaption>
      </figure>)}
    </div>}
  </>;
}
